/**
 * Represents a 2D vector.
 */
class Vector2 {
  /**
   * Creates an instance of Vector2.
   * @param {number} [x=0] - The x coordinate.
   * @param {number} [y=0] - The y coordinate.
   */
  constructor(x = 0, y = 0) {
    /** @type {number} */
    this.x = x;
    /** @type {number} */
    this.y = y;
  }

  set(x, y){
    this.x = x;
    this.y = y;
  }

  /**
   * Adds another vector to this vector.
   * @param {Vector2} v - The vector to add.
   * @returns {Vector2} - The resulting vector.
   */
  add(v) {
    return new Vector2(this.x + v.x, this.y + v.y);
  }

  /**
   * Subtracts another vector from this vector.
   * @param {Vector2} v - The vector to subtract.
   * @returns {Vector2} - The resulting vector.
   */
  subtract(v) {
    return new Vector2(this.x - v.x, this.y - v.y);
  }

  /**
   * Multiplies this vector by a scalar.
   * @param {number} scalar - The scalar to multiply by.
   * @returns {Vector2} - The resulting vector.
   */
  multiply(scalar) {
    return new Vector2(this.x * scalar, this.y * scalar);
  }

  /**
   * Divides this vector by a scalar.
   * @param {number} scalar - The scalar to divide by.
   * @returns {Vector2} - The resulting vector.
   */
  divide(scalar) {
    return new Vector2(this.x / scalar, this.y / scalar);
  }

  /**
   * Calculates the length (magnitude) of this vector.
   * @returns {number} - The length of the vector.
   */
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  /**
   * Normalizes this vector (makes it have a length of 1).
   * @returns {Vector2} - The normalized vector.
   */
  normalize() {
    const len = this.length();
    return len > 0 ? this.divide(len) : new Vector2(0, 0);
  }

  /**
   * Calculates the dot product of this vector and another vector.
   * @param {Vector2} v - The vector to dot with.
   * @returns {number} - The dot product.
   */
  dot(v) {
    return this.x * v.x + this.y * v.y;
  }
  /**
   * Calculates the distance between two vectors.
   * @param {Vector2} v1 - The first vector.
   * @param {Vector2} v2 - The second vector.
   * @returns {number} - The distance between the two vectors.
   */
  static Distance(v1, v2) {
    const dx = v2.x - v1.x;
    const dy = v2.y - v1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
  /**
   * Checks if two vectors are equal
   * @param {Vector2} v1 - The first vector.
   * @param {Vector2} v2 - The second vector.
   * @param {number} eps - The acceptable distance
   * @returns {boolean} - True if two vectors are roughly equal
   */
  static Equal(v1, v2, eps) {
    const dx = v2.x - v1.x;
    const dy = v2.y - v1.y;
    return Math.abs(dx) < eps && Math.abs(dy) < eps;
  }

  /**
   * changes size stored in vector with respect to aspect ratio. only newX or newY can be set at a time. Other is calculated using aspect ratio
   * @param {number | null} newX new X value
   * @param {number | null} newY new Y value
   */
  scaleSize(newX, newY){
    if (newX === null){
      this.x = newY * (this.x / this.y);
      this.y = newY;
    } else if (newY === null){
      this.y = newX * (this.y / this.x);  
      this.x = newX;
    }
  }

  copy(){
    return new Vector2(this.x, this.y);
  }

  /**
   * convets text size to real size. Ex: 100vh, 55 vw
   * @param {string} sizeX the uncorverted width
   * @param {string} sizeY the unconverted height
   * @param {HTMLElement} parentHTML html element used as a reference when working with percentages
   * @returns {Vector2} converted size
   */
  static sizeFromText (sizeX, sizeY, parentHTML=null){
    return new Vector2(
      Vector2.unit(sizeX, parentHTML),
      Vector2.unit(sizeY, parentHTML) 
    );
  }

  /**
   * Converts a text written unit to number. Ex. 23vh
   * @param {string} text unit to convert
   * @param {HTMLElement} parentHTML html element used as a reference when working with percentages
   * @returns {number} converted unit
   */
  static unit(text, parentHTML=null){
    let num = "", measure = "";
    for (let i = 0; i < text.length; i++){
      if (text[i] === '.' || (text[i] >= '0' && text[i] <= '9')){
        if (i === text.length - 1){
          num = text;
        }
      } else{
        num = text.substring(0, i);
        measure = text.substring(i);
        break;
      }
    }
    if (num.length === 0){
      console.warn(`Unable to process unit. Invalid number: "${num}". Measure: "${measure}"`);
      return 0;
    }
    num = Number(num);

    if (measure === "vh"){
      return num / 100 * document.body.clientHeight;
    }
    if (measure === "vw"){
      return num / 100 * document.body.clientWidth;
    }
    if (measure === "h%"){
      if (parentHTML === null){
        console.warn(`Unable to calculate percentage unit ${num}%`);
        return 0;
      }
      return num / 100 * parentHTML.getCanvasSize().y;
    }
    if (measure === "w%"){
      if (parentHTML === null){
        console.warn(`Unable to calculate percentage unit ${num}%`);
        return 0;
      }
      return num / 100 * parentHTML.getCanvasSize().x;
    }

    return num;
  }

  static distanceToLine(p, p1, p2) {
    const A = p.x - p1.x;
    const B = p.y - p1.y;
    const C = p2.x - p1.x;
    const D = p2.y - p1.y;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    const param = len_sq !== 0 ? dot / len_sq : -1;

    let nearestX, nearestY;

    if (param < 0) {
      nearestX = p1.x;
      nearestY = p1.y;
    } else if (param > 1) {
      nearestX = p2.x;
      nearestY = p2.y;
    } else {
      nearestX = p1.x + param * C;
      nearestY = p1.y + param * D;
    }

    const dx = p.x - nearestX;
    const dy = p.y - nearestY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}

/**
 * Represents a 3D vector.
 */
class Vector3 {
  /**
   * Creates an instance of Vector3.
   * @param {number} [x=0] - The x coordinate.
   * @param {number} [y=0] - The y coordinate.
   * @param {number} [z=0] - The z coordinate.
   */
  constructor(x = 0, y = 0, z = 0) {
    /** @type {number} */
    this.x = x;
    /** @type {number} */
    this.y = y;
    /** @type {number} */
    this.z = z;
  }

  /**
   * Adds another vector to this vector.
   * @param {Vector3} v - The vector to add.
   * @returns {Vector3} - The resulting vector.
   */
  add(v) {
    return new Vector3(this.x + v.x, this.y + v.y, this.z + v.z);
  }

  /**
   * Subtracts another vector from this vector.
   * @param {Vector3} v - The vector to subtract.
   * @returns {Vector3} - The resulting vector.
   */
  subtract(v) {
    return new Vector3(this.x - v.x, this.y - v.y, this.z - v.z);
  }

  /**
   * Multiplies this vector by a scalar.
   * @param {number} scalar - The scalar to multiply by.
   * @returns {Vector3} - The resulting vector.
   */
  multiply(scalar) {
    return new Vector3(this.x * scalar, this.y * scalar, this.z * scalar);
  }

  /**
   * Divides this vector by a scalar.
   * @param {number} scalar - The scalar to divide by.
   * @returns {Vector3} - The resulting vector.
   */
  divide(scalar) {
    return new Vector3(this.x / scalar, this.y / scalar, this.z / scalar);
  }

  /**
   * Calculates the length (magnitude) of this vector.
   * @returns {number} - The length of the vector.
   */
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }

  /**
   * Normalizes this vector (makes it have a length of 1).
   * @returns {Vector3} - The normalized vector.
   */
  normalize() {
    const len = this.length();
    return len > 0 ? this.divide(len) : new Vector3(0, 0, 0);
  }

  /**
   * Calculates the dot product of this vector and another vector.
   * @param {Vector3} v - The vector to dot with.
   * @returns {number} - The dot product.
   */
  dot(v) {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }

  /**
   * Calculates the cross product of this vector and another vector.
   * @param {Vector3} v - The vector to cross with.
   * @returns {Vector3} - The resulting vector.
   */
  cross(v) {
    return new Vector3(
      this.y * v.z - this.z * v.y,
      this.z * v.x - this.x * v.z,
      this.x * v.y - this.y * v.x
    );
  }

  
  /**
   * Checks if two vectors are equal
   * @param {Vector3} v1 - The first vector.
   * @param {Vector3} v2 - The second vector.
   * @param {number} eps - The acceptable distance
   * @returns {boolean} - True if two vectors are roughly equal
   */
  static Equal(v1, v2, eps) {
    const dx = v2.x - v1.x;
    const dy = v2.y - v1.y;
    const dz = v2.z - v1.z;
    return Math.abs(dx) < eps && Math.abs(dy) < eps && Math.abs(dz) < eps;
  }
  
  /**
   * calculates distance between vector3
   * @param {Vector3} vec1 
   * @param {Vector3} vec2 
   * @returns {Vector3}
   */
  static dist(vec1, vec2) {
      const dx = vec2.x - vec1.x;
      const dy = vec2.y - vec1.y;
      const dz = vec2.z - vec1.z;
      return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * moves vector3 towards vector3 with maximum distance
   * @param {Vector3} target target vector
   * @param {number} maxDistanceDelta max distance to cross
   * @returns {Vector3} moved vector
   */
  moveTowards(target, maxDistanceDelta) {
    const distance = Vector3.dist(this, target);

    if (distance <= 1 || (maxDistanceDelta >= 0 && distance <= maxDistanceDelta)) {
      this.x = target.x;
      this.y = target.y;
      this.z = target.z;
      return;
    }

    const t = maxDistanceDelta / distance;

    this.x += (target.x - this.x) * t;
    this.y += (target.y - this.y) * t;
    this.z += (target.z - this.z) * t;
  }

  copy(){
    return new Vector3(this.x, this.y, this.z);
  }
}




export {Vector2, Vector3};